// Care Team Sales — net revenue attributed to each practitioner in the
// window. Split proportionally across staff on multi-practitioner lines.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

export default function CareTeamSalesReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/care-team-sales`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers; refunds signed negative.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const signedMoney = (v) => (v < 0 ? `- ${money(Math.abs(v))}` : money(v));
    const num = { numeric: true, format: money, align: "right" };
    const signedNum = { numeric: true, format: signedMoney, align: "right" };
    const rows = (data.rows || []).map((r) => ({
      practitioner: r.staff_name,
      orders: r.order_count,
      lines: r.line_count,
      gross: Number(r.gross) || 0,
      refunds: r.refunds ? -Math.abs(Number(r.refunds)) : 0,
      net: Number(r.net) || 0,
    }));
    return {
      filename: "care-team-sales",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Practitioner", key: "practitioner" },
        { header: "Orders", key: "orders", align: "right" },
        { header: "Lines", key: "lines", align: "right" },
        { header: "Gross", key: "gross", ...num },
        { header: "Refunds", key: "refunds", ...signedNum },
        { header: "Net", key: "net", ...num },
      ],
      rows,
      footerRow: [
        "Totals", "", "",
        Number(data.total_gross) || 0,
        data.total_refunds ? -Math.abs(Number(data.total_refunds)) : 0,
        Number(data.total_net) || 0,
      ],
    };
  }, [data, branch]);

  return (
    <ReportShell title="Care Team Sales" subtitle="Net revenue attributed to each practitioner. Multi-staff lines are split proportionally." branchName={branch?.name} tab="care-team" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="care-team-sales-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">By Practitioner</h2>
              </div>
              {data && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">total · net</div>
                  <div className="text-xl font-semibold" data-testid="care-team-sales-total">{formatBranchMoney(branch, data.total_net)}</div>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">Practitioner</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Orders</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Lines</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Gross</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Refunds</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    {loading ? "Loading…" : "No sales in this range."}
                  </TableCell></TableRow>
                ) : data.rows.map((r) => (
                  <TableRow key={r.staff_id || "unassigned"} data-testid={`care-team-sales-row-${r.staff_id || "unassigned"}`}>
                    <TableCell className="font-medium text-sm">{r.staff_name}</TableCell>
                    <TableCell className="text-right text-sm">{r.order_count}</TableCell>
                    <TableCell className="text-right text-sm">{r.line_count}</TableCell>
                    <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.gross)}</TableCell>
                    <TableCell className="text-right text-sm">{r.refunds ? `- ${formatBranchMoney(branch, r.refunds)}` : formatBranchMoney(branch, 0)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatBranchMoney(branch, r.net)}</TableCell>
                  </TableRow>
                ))}
                {data && data.count > 0 && (
                  <TableRow className="bg-muted font-semibold">
                    <TableCell colSpan={3}>Totals</TableCell>
                    <TableCell className="text-right">{formatBranchMoney(branch, data.total_gross)}</TableCell>
                    <TableCell className="text-right">{data.total_refunds ? `- ${formatBranchMoney(branch, data.total_refunds)}` : formatBranchMoney(branch, 0)}</TableCell>
                    <TableCell className="text-right">{formatBranchMoney(branch, data.total_net)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
